import Parse from "parse";

import { endSession, endSessionElsewhere, isLostSession, isRenewableAuthFailure, renewAuthToken } from "./tokenRenewal";

let authErrorInterceptorInstalled = false;

/**
 * An expired bearer costs one renewal and retry rather than a failed call. The retry
 * goes to the default controller, so a second failure cannot loop back here.
 */
function installGlobalAuthErrorInterceptor() {
  if (authErrorInterceptorInstalled) return;
  authErrorInterceptorInstalled = true;

  const CoreManager = Parse.CoreManager as any;
  const DefaultController = CoreManager.getRESTController();

  CoreManager.setRESTController({
    ...DefaultController,
    async request(method: string, path: string, data: unknown, options?: unknown) {
      try {
        return await DefaultController.request(method, path, data, options);
      } catch (error: any) {
        // A dead session is ended, not renewed. Only a stale bearer is worth a retry.
        if (isLostSession(error)) {
          endSession();
          throw error;
        }

        if (!isRenewableAuthFailure(error)) throw error;

        const outcome = await renewAuthToken();
        if (outcome === "renewed") {
          return DefaultController.request(method, path, data, options);
        }

        // Only the server refusing ends the session. A renewal that merely failed —
        // offline, or the API mid-restart — leaves it alone; the next call tries again.
        if (outcome === "refused") endSession();
        throw error; // preserve each call site's own try/catch behavior
      }
    },
  });

  // Every check above reads storage, and a logout in another tab empties it, so this tab
  // would never match a lost session and would sit on a dead one. Hearing the session
  // token go is that logout, so end it here too.
  //
  // A token replaced is a fresh login there. Staying on the old session, this tab's
  // renewals would spend that login's refresh token and trip reuse detection, signing
  // both out. Reloading adopts the stored session through the normal restore; tearing
  // down instead would log out with the other tab's token and end its session.
  window.addEventListener("storage", (event) => {
    if (event.key !== "sessionToken" || !event.oldValue) return;
    if (!event.newValue) endSessionElsewhere();
    else if (event.newValue !== event.oldValue) window.location.reload();
  });
}

// Initialize Parse
const parseInitialize = async () => {
  const appId = process.env.REACT_APP_PARSE_APPLICATION_ID;
  const jsKey = process.env.REACT_APP_PARSE_JAVASCRIPT_KEY;
  const serverURL = process.env.REACT_APP_PARSE_SERVER_URL;

  if (!appId || !jsKey || !serverURL) {
    console.error("ParseClient initialization failed: Missing environment variables.");
    return;
  }

  Parse.initialize(appId, jsKey);
  Parse.serverURL = `${serverURL}/parse`;

  // Attach the login JWT as `Authorization: Bearer` on every Parse request.
  // Parse 4.2.0's RESTController merges CoreManager REQUEST_HEADERS into every
  // request, so this one line covers all Parse.Cloud.run / Parse.Query calls
  // (the backend withAuth reads this header). `authJwt` is the RS256 JWT from
  // login, distinct from `sessionToken` (the _Session token used by
  // /auth validate/logout/dfns routes).
  const authJwt = localStorage.getItem("authJwt");
  Parse.CoreManager.set("REQUEST_HEADERS", authJwt ? { Authorization: `Bearer ${authJwt}` } : {});

  installGlobalAuthErrorInterceptor();

  // Hydrate Parse.User.current() from the _Session token (used by flows that
  // read the current user). Auth for cloud calls rides on the JWT header above.
  const sessionToken = localStorage.getItem("sessionToken");
  if (sessionToken) {
    try {
      await Parse.User.become(sessionToken);
    } catch (error) {
      console.error("Error becoming user with sessionToken:", error);
    }
  }
};

export default parseInitialize;
